package com.advaita.cloud.drive.ui.components

import androidx.compose.runtime.Composable
import com.advaita.cloud.drive.shared.AccessGrantDto
import com.advaita.cloud.drive.shared.UserInfoDto
import com.advaita.cloud.drive.ui.api.ShareApiException
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text

/**
 * ADV-1330 F4 (ruling S8): the nine capped states of the share UI. A cap is a disabled control
 * with ONE sentence of reason. It is never an error message, never red, and it never names a
 * status code. The ceiling sentence stays generic on purpose.
 *
 * | Row | Cap | Signal |
 * |---|---|---|
 * | 1 | [CHECKS_OFF] | `/api/me` `sharingEnabled: false`, or `/api/shared` `404`. |
 * | 2 | [NOT_REGISTERED] | `409 sharing_not_ready`. |
 * | 3 | [OUTAGE] | `503 sharing_unavailable`. |
 * | 4 | [ORG_POLICY] | `403 capability_denied`. |
 * | 5 | [DOMAIN_FALLBACK] | `/api/me` `orgSource` not `ORG_CLAIM`, or `403 organization_account_required`. |
 * | 6 | [NOT_SIGNED_IN] | a picker row with `signedIn: false`, or `422 unknown_principal`. |
 * | 7 | [ABOVE_OWN_LEVEL] | `403 capabilities_exceed_authority`. |
 * | 8 | [NOT_ON_CHAIN] | a grant with `relevelable` or `removable` false, or `403 share_authority_required`. |
 * | 9 | [PUBLIC_MAIL] | `/api/me` `sharingAllowed: false` on an `ORG_CLAIM` session, or `403 sharing_not_allowed` (D7; absent means not allowed). |
 */
enum class ShareCap {
    CHECKS_OFF,
    NOT_REGISTERED,
    OUTAGE,
    ORG_POLICY,
    DOMAIN_FALLBACK,
    NOT_SIGNED_IN,
    ABOVE_OWN_LEVEL,
    NOT_ON_CHAIN,
    PUBLIC_MAIL,

    /**
     * Not an S8 row: the caller can read the item but holds no `share`, so the dialog has
     * nothing to offer (`403 share_not_held`, coordinator decision (a)). Its own sentence, so a
     * viewer never reads the org-ceiling sentence of row 4.
     */
    SHARE_NOT_HELD,
}

/**
 * The one sentence for [cap]. Row 6 names the person ([person]) and row 8 names the owner
 * ([owner]). Without a name, each still reads as one sentence.
 */
fun capSentence(cap: ShareCap, owner: String? = null, person: String? = null): String = when (cap) {
    ShareCap.CHECKS_OFF -> "Sharing is not on yet in this environment."
    ShareCap.NOT_REGISTERED -> "DataHub is still preparing this item for sharing. Try again in a minute."
    ShareCap.OUTAGE -> "Sharing is temporarily unavailable. Your own files still work."
    ShareCap.ORG_POLICY -> "Your organization does not allow this action."
    ShareCap.DOMAIN_FALLBACK -> "Sharing needs your organization account. Sign out and sign in again."
    ShareCap.NOT_SIGNED_IN -> "${person ?: "This person"} has not signed in to DataHub yet."
    ShareCap.ABOVE_OWN_LEVEL -> "You can give only the access that you hold."
    ShareCap.NOT_ON_CHAIN -> "Only ${owner ?: "the owner"} or the person who shared this can change it."
    ShareCap.PUBLIC_MAIL -> "Sharing is not available for personal email accounts."
    ShareCap.SHARE_NOT_HELD -> "Only people who can share this item can see or change who has access."
}

/**
 * True when the share UI shows at all: `/api/me` has answered and says `sharingEnabled`. Before
 * it answers, or when it failed, the UI shows no Share control and no Shared with me tab, and
 * never the row 1 sentence (the frontend build ruling, item 1). Example: before go-live, Priya's
 * My Drive shows no Share icon at all.
 */
fun shareUiShown(me: UserInfoDto?): Boolean = me?.sharingEnabled == true

/**
 * ADV-1330 D7 Q1 (Tim, 2026-09-28): true when the Share control shows. It needs [shareUiShown],
 * and it hides for an [UserInfoDto.ORG_CLAIM] session whose token carried no `org_shareable`
 * claim (an az2 that predates #35). A `false` claim keeps the control, capped with row 9. The
 * Shared with me tab reads [shareUiShown] alone. Example: Priya, against an az2 without #35,
 * sees no Share icon. Gil (gmail.com) sees it greyed, with "Sharing is not available for
 * personal email accounts."
 */
fun shareControlShown(me: UserInfoDto?): Boolean =
    // [capForMe] reads `sharingAllowed` and this reads `orgShareable`, so the two must agree.
    me != null && shareUiShown(me) && !(me.orgSource == UserInfoDto.ORG_CLAIM && me.orgShareable == null)

/**
 * The cap that the session itself puts on every Share control, or null. The gate wins (row 1;
 * the UI hides the controls then, see [shareUiShown]), then row 5, then the public-mail rule
 * (row 9). The backend refuses every share route with `403 organization_account_required` for
 * any session whose org did not come from the token's `org_id` claim (the domain fallback, or no
 * source at all), whatever `tenancyExpected` says (coordinator decision (d)). So the UI caps
 * every session that is not [UserInfoDto.ORG_CLAIM] with row 5. Such a session never carries
 * the claim, so `sharingAllowed` is false for it too, and row 5 must come first. Then
 * `sharingAllowed: false` is row 9 (D7). Null [me] is no cap: nothing shows yet.
 */
fun capForMe(me: UserInfoDto?): ShareCap? = when {
    me == null -> null
    !me.sharingEnabled -> ShareCap.CHECKS_OFF
    me.orgSource != UserInfoDto.ORG_CLAIM -> ShareCap.DOMAIN_FALLBACK
    !me.sharingAllowed -> ShareCap.PUBLIC_MAIL
    else -> null
}

/**
 * The cap for a refused share call, or null when the refusal is not a cap (an error or a race).
 * [notFoundIsGate] is for a route whose only `404` is the gate: `/api/people`. There a `404` means
 * the gate shut while the UI still believed sharing was on, which is row 1.
 */
fun capForError(e: ShareApiException, notFoundIsGate: Boolean = false): ShareCap? = when {
    e.status == 404 && notFoundIsGate -> ShareCap.CHECKS_OFF
    e.status == 503 -> ShareCap.OUTAGE
    e.status == 409 && e.code == "sharing_not_ready" -> ShareCap.NOT_REGISTERED
    e.status == 403 && e.code == "capability_denied" -> ShareCap.ORG_POLICY
    e.status == 403 && e.code == "organization_account_required" -> ShareCap.DOMAIN_FALLBACK
    e.status == 403 && e.code == "sharing_not_allowed" -> ShareCap.PUBLIC_MAIL
    e.status == 422 && e.code == "unknown_principal" -> ShareCap.NOT_SIGNED_IN
    e.status == 403 && e.code == "capabilities_exceed_authority" -> ShareCap.ABOVE_OWN_LEVEL
    e.status == 403 && e.code == "share_authority_required" -> ShareCap.NOT_ON_CHAIN
    e.status == 403 && e.code == "share_not_held" -> ShareCap.SHARE_NOT_HELD
    else -> null
}

/** `GET /api/shared`: a `404` there means the checks are off (row 1). Else as [capForError]. */
fun capForSharedList(e: ShareApiException): ShareCap? = capForError(e, notFoundIsGate = true)

/** Row 8 on one grant: the caller may not change it. The caller's own grant is still Leave. */
fun capForGrant(g: AccessGrantDto): ShareCap? =
    if (!g.relevelable || (!g.removable && !g.self)) ShareCap.NOT_ON_CHAIN else null

/**
 * The rendered cap: one sentence, in the muted notice style. Never the red error style. [id]
 * lets the capped control point at the sentence with `aria-describedby`.
 */
@Composable
fun CappedNotice(cap: ShareCap, owner: String? = null, person: String? = null, id: String? = null) {
    Div(attrs = {
        classes("capped-notice")
        if (id != null) attr("id", id)
        attr("data-cap", cap.name)
        attr("role", "status")
    }) { Text(capSentence(cap, owner, person)) }
}
